import React, { useContext, useMemo, useState } from 'react';
import { AppContext } from '@/contexts/AppContext';

interface ProductFormState {
  id?: number;
  name: string;
  description: string;
  imageUrl: string;
  imageFile: File | null;
  price: string;
  categoryId: number | '';
  stockQuantity: number;
  isActive: boolean;
  isAvailable: boolean;
}

const createEmptyProductFormState = (defaultCategoryId?: number): ProductFormState => ({
  id: undefined,
  name: '',
  description: '',
  imageUrl: '',
  imageFile: null,
  price: '',
  categoryId: defaultCategoryId ?? '',
  stockQuantity: 0,
  isActive: true,
  isAvailable: true,
});

const ProductManagement: React.FC = () => {
  const context = useContext(AppContext);

  if (!context) {
    throw new Error('ProductManagement deve ser usado dentro de AppProvider');
  }

  const {
    adminProducts,
    productCategories,
    addProduct,
    updateProduct,
    deleteProduct,
    toggleProductStock,
    parsePrice,
  } = context;

  const categoryOptions = useMemo(
    () => productCategories.map(category => ({ value: category.id, label: category.name })),
    [productCategories],
  );

  const [formState, setFormState] = useState<ProductFormState | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searchId, setSearchId] = useState('');
  const [searchName, setSearchName] = useState('');
  const [searchCategoryId, setSearchCategoryId] = useState('');
  const [searchStatus, setSearchStatus] = useState<'all' | 'available' | 'unavailable'>('all');

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setFormState(prev => (prev ? { ...prev, imageFile: file } : prev));
  };

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = event.target;
    setFormState(prev => {
      if (!prev) return prev;
      if (name === 'categoryId') {
        return { ...prev, categoryId: value ? Number(value) : '' };
      }
      if (name === 'stockQuantity') {
        return { ...prev, stockQuantity: Number(value) };
      }
      if (name === 'price') {
        return { ...prev, price: value };
      }
      return { ...prev, [name]: value };
    });
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    setFormState(prev => (prev ? { ...prev, [name]: checked } : prev));
  };

  const startAdding = () => {
    const defaultCategoryId = categoryOptions[0]?.value;
    setFormState(createEmptyProductFormState(defaultCategoryId));
    setFeedback(null);
    setError(null);
  };

  const startEditing = (productId: number) => {
    const product = adminProducts.find(item => item.id === productId);
    if (!product) return;

    setFormState({
      id: product.id,
      name: product.name,
      description: product.description,
      imageUrl: product.imageUrl ?? '',
      imageFile: null,
      price:
        product.priceNumber != null
          ? product.priceNumber.toString()
          : parsePrice(product.price).toString(),
      categoryId: product.categoryId ?? '',
      stockQuantity: product.stockQuantity ?? 0,
      isActive: product.isActive ?? true,
      isAvailable: product.isAvailable ?? product.inStock ?? true,
    });
    setFeedback(null);
    setError(null);
  };

  const cancelEditing = () => {
    setFormState(null);
    setFeedback(null);
    setError(null);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!formState) return;

    const normalizedPrice = Number(String(formState.price).replace(',', '.'));
    if (Number.isNaN(normalizedPrice) || normalizedPrice < 0) {
      setError('Informe um preço válido.');
      return;
    }

    if (formState.categoryId === '') {
      setError('Selecione uma categoria para o produto.');
      return;
    }

    setSubmitting(true);
    setError(null);
    setFeedback(null);

    const payload = {
      name: formState.name.trim(),
      description: formState.description.trim(),
      // Só usa URL manual se não houver arquivo selecionado
      imageUrl: !formState.imageFile && formState.imageUrl.trim() ? formState.imageUrl.trim() : undefined,
      imageFile: formState.imageFile ?? undefined,
      price: normalizedPrice,
      categoryId: formState.categoryId,
      stockQuantity: Number.isFinite(formState.stockQuantity) ? formState.stockQuantity : undefined,
      isActive: formState.isActive,
      isAvailable: formState.isAvailable,
    } as const;

    try {
      if (formState.id && formState.id > 0) {
        await updateProduct(formState.id, payload);
        setFeedback('Produto atualizado com sucesso.');
      } else {
        await addProduct(payload);
        setFeedback('Produto criado com sucesso.');
      }
      setFormState(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao salvar produto.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (productId: number) => {
    const confirmation = window.confirm('Tem certeza que deseja remover este produto?');
    if (!confirmation) return;

    setSubmitting(true);
    setError(null);
    setFeedback(null);
    try {
      await deleteProduct(productId);
      setFeedback('Produto removido com sucesso.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao remover produto.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStock = async (productId: number) => {
    setSubmitting(true);
    setError(null);
    setFeedback(null);
    try {
      await toggleProductStock(productId);
      setFeedback('Status do produto atualizado.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao alterar status do produto.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewAndEditImage = (productId: number) => {
    const product = adminProducts.find(item => item.id === productId);
    if (!product) return;

    if (product.imageUrl) {
      window.open(product.imageUrl, '_blank');
    }

    startEditing(productId);

    setTimeout(() => {
      const input = document.querySelector<HTMLInputElement>('input[name="imageUrl"]');
      if (input) {
        input.focus();
      }
    }, 0);
  };

  const filteredProducts = useMemo(() => {
    return adminProducts.filter(product => {
      if (searchId.trim()) {
        const parsedId = Number(searchId.trim());
        if (!Number.isNaN(parsedId) && product.id !== parsedId) {
          return false;
        }
      }

      if (searchName.trim()) {
        const term = searchName.trim().toLowerCase();
        if (!product.name.toLowerCase().includes(term)) {
          return false;
        }
      }

      if (searchCategoryId) {
        const categoryId = Number(searchCategoryId);
        if (!Number.isNaN(categoryId) && product.categoryId !== categoryId) {
          return false;
        }
      }

      if (searchStatus !== 'all') {
        const isAvailable = (product.isAvailable ?? product.inStock ?? true) as boolean;
        if (searchStatus === 'available' && !isAvailable) {
          return false;
        }
        if (searchStatus === 'unavailable' && isAvailable) {
          return false;
        }
      }

      return true;
    });
  }, [adminProducts, searchId, searchName, searchCategoryId, searchStatus]);

  return (
    <div className="admin-section">
      <div className="section-header">
        <h2>Gerenciamento de Produtos</h2>
        <button
          type="button"
          className="add-button"
          onClick={startAdding}
          disabled={submitting}
        >
          Adicionar Produto
        </button>
      </div>

      {error && <p className="error">{error}</p>}
      {feedback && <p className="success">{feedback}</p>}

      {formState && (
        <form onSubmit={handleSubmit} className="admin-form admin-product-form">
          {/* Linha 1: Nome (coluna 1) + Categoria (coluna 2) */}
          <div className="admin-product-row">
            <div className="admin-product-column">
              <span className="admin-product-label admin-product-label--name">Nome do produto</span>
              <input
                name="name"
                value={formState.name}
                onChange={handleInputChange}
                placeholder="Ex: Cabana Málaga Fábrica Pet Verde Único"
                required
                disabled={submitting}
              />
            </div>
            <div className="admin-product-column">
              <span className="admin-product-label admin-product-label--category">Categoria</span>
              <select
                name="categoryId"
                value={formState.categoryId}
                onChange={handleInputChange}
                required
                disabled={submitting || categoryOptions.length === 0}
              >
                <option value="">Selecione uma categoria</option>
                {categoryOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Linha 2: Descrição (largura inteira) */}
          <div className="admin-product-row">
            <div className="admin-product-column admin-product-column--full">
              <span className="admin-product-label admin-product-label--full">Descrição</span>
              <textarea
                name="description"
                value={formState.description}
                onChange={handleInputChange}
                placeholder="Descrição detalhada do produto"
                required
                disabled={submitting}
              />
            </div>
          </div>

          {/* Linha 3b: Upload de imagem (largura inteira) */}
          <div className="admin-product-row">
            <div className="admin-product-column admin-product-column--full">
              <span className="admin-product-label admin-product-label--full">Upload de imagem (opcional)</span>
              <input
                type="file"
                name="imageFile"
                accept="image/*"
                onChange={handleFileChange}
                disabled={submitting}
              />
            </div>
          </div>

          {/* Linha 3: URL atual da imagem (opcional) */}
          <div className="admin-product-row">
            <div className="admin-product-column admin-product-column--full">
              <span className="admin-product-label admin-product-label--full">URL atual da imagem (opcional)</span>
              <input
                name="imageUrl"
                value={formState.imageUrl}
                onChange={handleInputChange}
                placeholder="https://exemplo.com/imagem-do-produto.jpg"
                disabled={submitting}
              />
            </div>
          </div>

          {/* Linha 4: Preço (coluna 1) + Quantidade (coluna 2) */}
          <div className="admin-product-row admin-product-row--price-stock">
            <div className="admin-product-column">
              <span className="admin-product-label admin-product-label--price">Preço (R$)</span>
              <input
                name="price"
                value={formState.price}
                onChange={handleInputChange}
                placeholder="Ex: 49.90"
                required
                disabled={submitting}
              />
            </div>
            <div className="admin-product-column">
              <span className="admin-product-label admin-product-label--stock">Quantidade em estoque</span>
              <input
                name="stockQuantity"
                type="number"
                min={0}
                value={Number.isFinite(formState.stockQuantity) ? formState.stockQuantity : 0}
                onChange={handleInputChange}
                placeholder="Ex: 10"
                disabled={submitting}
              />
            </div>
          </div>

          {/* Linha 5: Checkboxes (Ativo / Disponível para venda) */}
          <div className="admin-product-row admin-product-row--toggles">
            <div className="admin-product-toggles">
              <label className="admin-toggle">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formState.isActive}
                  onChange={handleCheckboxChange}
                  disabled={submitting}
                />
                <span>Ativo</span>
              </label>
              <label className="admin-toggle">
                <input
                  type="checkbox"
                  name="isAvailable"
                  checked={formState.isAvailable}
                  onChange={handleCheckboxChange}
                  disabled={submitting}
                />
                <span>Disponível para venda</span>
              </label>
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="save-button" disabled={submitting}>
              {formState.id ? 'Atualizar' : 'Adicionar'}
            </button>
            <button type="button" className="cancel-button" onClick={cancelEditing} disabled={submitting}>
              Cancelar
            </button>
          </div>
        </form>
      )}

      {categoryOptions.length === 0 && (
        <p className="warning">Cadastre categorias no backend para associá-las aos produtos.</p>
      )}

      <div className="admin-filters">
        <div className="admin-filters-row">
          <div className="admin-filters-field">
            <label htmlFor="filter-id">ID</label>
            <input
              id="filter-id"
              type="number"
              value={searchId}
              onChange={event => setSearchId(event.target.value)}
              placeholder="Filtrar por ID"
            />
          </div>
          <div className="admin-filters-field">
            <label htmlFor="filter-name">Nome</label>
            <input
              id="filter-name"
              type="text"
              value={searchName}
              onChange={event => setSearchName(event.target.value)}
              placeholder="Filtrar por nome"
            />
          </div>
          <div className="admin-filters-field">
            <label htmlFor="filter-category">Categoria</label>
            <select
              id="filter-category"
              value={searchCategoryId}
              onChange={event => setSearchCategoryId(event.target.value)}
            >
              <option value="">Todas</option>
              {categoryOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="admin-filters-field">
            <label htmlFor="filter-status">Status</label>
            <select
              id="filter-status"
              value={searchStatus}
              onChange={event => setSearchStatus(event.target.value as 'all' | 'available' | 'unavailable')}
            >
              <option value="all">Todos</option>
              <option value="available">Disponível</option>
              <option value="unavailable">Indisponível</option>
            </select>
          </div>
        </div>
      </div>

      <table className="admin-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Nome</th>
            <th>Preço</th>
            <th>Categoria</th>
            <th>Status</th>
            <th>Ações</th>
          </tr>
        </thead>
        <tbody>
          {filteredProducts.map(product => (
            <tr key={product.id}>
              <td>{product.id}</td>
              <td>{product.name}</td>
              <td>{product.price}</td>
              <td>{product.category}</td>
              <td>{(product.isAvailable ?? product.inStock) ? 'Disponível' : 'Indisponível'}</td>
              <td className="actions">
                <div className="admin-actions">
                  <button
                    type="button"
                    className="icon-button icon-button--image"
                    onClick={() => handleViewAndEditImage(product.id)}
                    disabled={submitting}
                    title={product.imageUrl ? 'Ver e editar imagem do produto' : 'Definir imagem do produto'}
                  >
                    🖼️
                    <span className="sr-only">Ver e editar imagem</span>
                  </button>
                  <button
                    type="button"
                    className="icon-button icon-button--edit"
                    onClick={() => startEditing(product.id)}
                    disabled={submitting}
                    title="Editar produto"
                  >
                    ✏️
                    <span className="sr-only">Editar</span>
                  </button>
                  <button
                    type="button"
                    className={`icon-button icon-button--toggle ${(product.isActive ?? true) ? 'is-active' : 'is-inactive'}`}
                    onClick={() => handleToggleStock(product.id)}
                    disabled={submitting}
                    title={(product.isActive ?? true) ? 'Desativar produto' : 'Ativar produto'}
                  >
                    {(product.isActive ?? true) ? '⏸' : '▶'}
                    <span className="sr-only">
                      {(product.isActive ?? true) ? 'Desativar' : 'Ativar'}
                    </span>
                  </button>
                  <button
                    type="button"
                    className="icon-button icon-button--delete"
                    onClick={() => handleDelete(product.id)}
                    disabled={submitting}
                    title="Remover produto"
                  >
                    🗑️
                    <span className="sr-only">Remover</span>
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ProductManagement;